import { IngredientReal } from '@cf-shared/data-model/ingredient';
import { LL, BOOT_LOCALE } from 'i18n/i18n.service';
import { TranslatedString } from 'models/translated-string';
import type { TranslationFunctions } from '../../../gensrc/i18n/i18n-types';
import { displayRefusePercent, isRoughRefuse, refusePartsText, usableRefusePercent } from './refuse-hint';

/**
 * The "Weighed with peel / pit / bone" checkbox on the amount screen (#5544).
 *
 * A ticked row keeps the EATEN weight in `weight_g` and stores only the uneaten share it was
 * converted with (`IngredientReal.weighedWithRefuse`). The weight as typed is never stored; it is
 * worked back from those two numbers, so it cannot disagree with `weight_g` after auto-tune, a split
 * or a copy changes the weight. See design-docs/food-staples.md, "Weighed with the uneaten part".
 */

export { usableRefusePercent };

/** the weight eaten out of a weight taken with the uneaten part */
export function eatenWeight(typed_g: number, percent: number): number {
  return typed_g * (1 - percent / 100);
}

/** the weight with the uneaten part, worked back from the weight eaten */
export function typedWeight(eaten_g: number, percent: number): number {
  return eaten_g / (1 - percent / 100);
}

/**
 * Read the stored flag off an ingredient. The row may have been written by another app version, or be
 * damaged, so the flag counts only when its percent is above 0 and below 100. Anything else present
 * reads as unticked and is marked damaged, so the caller can report it; no app version writes such a
 * value, so it cannot be an old shape.
 */
export function readWeighedWithRefuse(ingredient: IngredientReal): { percent: number | undefined; damaged: boolean } {
  const flag: unknown = ingredient.weighedWithRefuse;
  if (flag === undefined) {
    return { percent: undefined, damaged: false };
  }
  const percent = usableRefusePercent(flag);
  return { percent, damaged: percent === undefined };
}

/** the checkbox label: "Weighed with peel", or "Weighed as bought" when the food names no part */
export function buildWeighedWithLabel(refuse: unknown, ll: TranslationFunctions = LL, locale: string = BOOT_LOCALE): TranslatedString {
  const partsText = refusePartsText(refuse, ll, locale);
  return partsText !== undefined
    ? ll.editIngredient.weighedWith.withParts({ parts: partsText })
    : ll.editIngredient.weighedWith.asBought();
}

/**
 * The line under a ticked checkbox: how much is counted, and how much was taken off. `eatenText` is
 * the eaten weight already formatted in the user's weight unit. A rough share (it depends on the cut
 * or how the food is trimmed) is worded as an estimate, the same way the hint words it.
 */
export function buildWeighedWithNote(eatenText: string, percent: number, refuse: unknown, ll: TranslationFunctions = LL): TranslatedString {
  const shownPercent = displayRefusePercent(percent);
  return isRoughRefuse(refuse)
    ? ll.editIngredient.weighedWithNote.rough({ weight: eatenText, percent: shownPercent })
    : ll.editIngredient.weighedWithNote.typical({ weight: eatenText, percent: shownPercent });
}
